import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, RotateCw, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/permissions";

export default function SettingsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Configuration</div>
          <h1 className="font-serif text-3xl lg:text-4xl font-medium">Settings</h1>
        </div>
        <Button variant="outline" onClick={() => nav("/settings/roles")} className="rounded-full" data-testid="view-roles-btn">
          <ShieldCheck size={14} className="mr-1"/> View Role Matrix
        </Button>
      </div>

      <Tabs defaultValue="categories" className="w-full">
        <TabsList className="bg-[#F1EDDF]">
          <TabsTrigger value="categories">Product Categories</TabsTrigger>
          <TabsTrigger value="measurements">Master Measurements</TabsTrigger>
          {isAdmin && <TabsTrigger value="users">Users</TabsTrigger>}
        </TabsList>
        <TabsContent value="categories"><CategoriesTab/></TabsContent>
        <TabsContent value="measurements"><MeasurementsTab/></TabsContent>
        {isAdmin && <TabsContent value="users"><UsersTab/></TabsContent>}
      </Tabs>
    </div>
  );
}

function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", measurement_type: "" });

  const load = async () => setItems((await api.get("/product-categories")).data);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.name || !f.measurement_type) return toast.error("All fields required");
    try { await api.post("/product-categories", f); toast.success("Added"); setF({ name: "", measurement_type: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/product-categories/${id}`); load(); };

  return (
    <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label>Name</Label><Input value={f.name} onChange={(e)=>setF({...f, name:e.target.value})} placeholder="e.g. Ring" className="mt-1" data-testid="cat-name-input"/></div>
        <div><Label>Measurement type</Label><Input value={f.measurement_type} onChange={(e)=>setF({...f, measurement_type:e.target.value})} placeholder="ring_size" className="mt-1"/></div>
        <div className="flex items-end"><Button onClick={add} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="add-cat-btn"><Plus size={14} className="mr-1"/> Add</Button></div>
      </div>
      <div className="divide-y divide-[#E4DFCF]">
        {items.map(c => (
          <div key={c.id} className="flex items-center justify-between py-2">
            <div>
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-[#4A5A50] uppercase tracking-widest">{c.measurement_type}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => del(c.id)} className="text-red-600"><Trash2 size={14}/></Button>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[#4A5A50] py-3">No categories yet</div>}
      </div>
    </CardContent></Card>
  );
}

function MeasurementsTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ type: "", value: "" });

  const load = async () => setItems((await api.get("/measurements")).data);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.type || !f.value) return toast.error("All fields required");
    try { await api.post("/measurements", f); setF({ type: "", value: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/measurements/${id}`); load(); };

  const grouped = items.reduce((acc, m) => { (acc[m.type] = acc[m.type] || []).push(m); return acc; }, {});

  return (
    <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label>Type</Label><Input value={f.type} onChange={(e)=>setF({...f, type:e.target.value})} placeholder="ring_size" className="mt-1" data-testid="meas-type-input"/></div>
        <div><Label>Value</Label><Input value={f.value} onChange={(e)=>setF({...f, value:e.target.value})} placeholder="16" className="mt-1"/></div>
        <div className="flex items-end"><Button onClick={add} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="add-meas-btn"><Plus size={14} className="mr-1"/> Add</Button></div>
      </div>
      {Object.entries(grouped).map(([type, arr]) => (
        <div key={type}>
          <div className="text-xs uppercase tracking-widest text-[#4A5A50] mt-2 mb-1">{type}</div>
          <div className="flex flex-wrap gap-2">
            {arr.map(m => (
              <div key={m.id} className="text-sm px-3 py-1.5 rounded-full bg-[#F1EDDF] border border-[#E4DFCF] flex items-center gap-2">
                {m.value}
                <button onClick={() => del(m.id)} className="text-red-600"><Trash2 size={12}/></button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </CardContent></Card>
  );
}

function UsersTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", email: "", role: "order_team", department: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => setItems((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  // Generate a friendly temporary password. Admin can override or accept.
  const genPassword = () => {
    const words = ["gold", "topaz", "ruby", "opal", "pearl", "jade", "onyx", "coral"];
    const w = words[Math.floor(Math.random() * words.length)];
    const n = Math.floor(100 + Math.random() * 900);
    return `${w}${n}`;
  };

  // Every time the email changes and password is empty, suggest one.
  const setEmail = (email) => {
    setF((prev) => ({
      ...prev,
      email,
      password: prev.password || genPassword(),
    }));
  };

  const clearForm = () => setF({ name: "", email: "", role: "order_team", department: "", password: "" });

  const add = async () => {
    if (!f.name || !f.email) return toast.error("Name and email are required");
    const payload = { ...f, password: f.password || genPassword() };
    if (!payload.department) delete payload.department;
    setBusy(true);
    try {
      await api.post("/users", payload);
      toast.success(`User created — password: ${payload.password}`);
      clearForm();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onEnter = (e) => { if (e.key === "Enter" && !busy) add(); };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate user?")) return;
    await api.delete(`/users/${id}`); load();
  };

  const rename = async (u) => {
    const next = window.prompt(`Rename '${u.name}'`, u.name || "");
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === (u.name || "")) return;
    try {
      await api.patch(`/users/${u.id}`, { name: trimmed });
      toast.success(`Renamed to ${trimmed}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rename failed");
    }
  };

  const showDepartment = f.role === "production" || f.role === "karigar";
  const ROLE_OPTIONS = ["admin","manager","order_team","sales","accounts","production","cad_designer","watchman","client"];

  return (
    <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6 space-y-4">
      <div className="text-xs text-[#4A5A50]">Quick add — tab through fields, press Enter to save. A temporary password is auto-suggested.</div>
      <div className={`grid grid-cols-1 gap-3 ${showDepartment ? "md:grid-cols-6" : "md:grid-cols-5"}`}>
        <div><Label>Name</Label><Input value={f.name} onChange={(e)=>setF({...f, name:e.target.value})} onKeyDown={onEnter} className="mt-1" data-testid="user-name-input" placeholder="Full name" autoFocus/></div>
        <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e)=>setEmail(e.target.value)} onKeyDown={onEnter} className="mt-1" placeholder="user@company.in"/></div>
        <div><Label>Role</Label>
          <Select value={f.role} onValueChange={(v)=>setF({...f, role:v, department: (v === "production" ? "Casting" : "")})}>
            <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {showDepartment && (
          <div><Label>Department</Label>
            <Select value={f.department || "Casting"} onValueChange={(v)=>setF({...f, department:v})}>
              <SelectTrigger className="mt-1" data-testid="user-department-select"><SelectValue/></SelectTrigger>
              <SelectContent>{["Casting","Filing","Setting","Polish","QC","Tagging","Dispatch"].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Password</Label>
          <div className="flex gap-1 mt-1">
            <Input
              type="text"
              value={f.password}
              onChange={(e)=>setF({...f, password:e.target.value})}
              onKeyDown={onEnter}
              placeholder="auto"
              className="flex-1"
              data-testid="user-password-input"
            />
            <Button type="button" variant="outline" size="sm" className="px-2 shrink-0" onClick={()=>setF({...f, password: genPassword()})} title="Generate">
              <RotateCw size={14}/>
            </Button>
          </div>
        </div>
        <div className="flex items-end"><Button onClick={add} disabled={busy} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full w-full" data-testid="add-user-btn"><Plus size={14} className="mr-1"/> Add User</Button></div>
      </div>
      <div className="divide-y divide-[#E4DFCF]">
        {items.map(u => (
          <div key={u.id} className="flex items-center justify-between py-2" data-testid={`user-row-${u.email}`}>
            <div>
              <div className="font-medium">{u.name}</div>
              <div className="text-xs text-[#4A5A50]">{u.email} · {ROLE_LABELS[u.role] || u.role}{u.department ? ` · ${u.department}` : ""} · {u.status}</div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => rename(u)} className="text-[#0F5E39]" title="Rename" data-testid={`user-rename-${u.email}`}><Pencil size={14}/></Button>
              <Button variant="ghost" size="sm" onClick={() => deactivate(u.id)} className="text-red-600" title="Deactivate" data-testid={`user-deactivate-${u.email}`}><Trash2 size={14}/></Button>
            </div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}
